<template>
  <div class="sorter-builder">
    <m-draggable
      v-if="orders.length > 0"
      class="_items"
      draggable-item-class="_handle"
      ghost-item-class="sorter-item"
      dragover-item-class="sorter-item"
      scroll-container-class="m-card"
      can-drag-over-top
      can-drag-over-bottom
      :disabled="disabled || mappedOrders.length === 1"
      :recreate-key="mappedOrders.map(({ key }) => key)"
      @set-drag-item="setDragItem"
      @over-top="setOverTop"
      @over-bottom="setOverBottom"
      @drag-drop="handleDrop"
      @cancel="cancelDragging"
    >
      <sorter-item
        v-for="(order, index) in mappedOrders"
        :key="order.key"
        class="_item"
        :data-id="order.key"
        :order="order"
        :orders="orders"
        :sort-options="fixedSortOptions"
        :disabled="disabled"
        :sort-disabled="disabled || mappedOrders.length === 1"
        :dragging-over-bottom="dragItemId !== '' && draggingOverBottom.includes(order.key)"
        :dragging-over-top="index === 0 && dragItemId !== '' && draggingOverTop.includes(order.key)"
        @change="change(index, $event)"
        @delete="deleteOrder(index)"
      />
    </m-draggable>
    <div
      v-if="!disabled && hasAddableEdge"
      class="_add"
    >
      <m-btn
        v-if="fixedSortOptions.length > value.length"
        class="_btn"
        icon="plus"
        small
        light
        hide-border
        @click="add"
      >
        {{ $t('sorterBuilder.add') }}
      </m-btn>
    </div>
    <div
      v-if="disabled && orders.length === 0"
      class="_empty"
    >
      {{ $t('sorterBuilder.empty') }}
    </div>
  </div>
</template>

<script>
import SorterItem from '@/components/sorter/SorterItem.vue';
import useSort from '@/composables/draggable/sort';
import { copy } from 'shared/lib/copy';
import { isEqual } from 'lodash-es';
import { orderEntitiesEqual } from '@/lib/sort';

export default {
  name: 'SorterBuilder',
  props: {
    value: {
      type: Array,
      default: () => [],
    },
    sortOptions: {
      type: Array,
      default: () => [],
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  setup() {
    const {
      setDragItem,
      setOverBottom,
      setOverTop,
      draggingOverBottom,
      draggingOverTop,
      dropItem,
      cancelDragging,
      dragItemId,
    } = useSort('key');

    return {
      setDragItem,
      setOverTop,
      setOverBottom,
      cancelDragging,
      draggingOverBottom,
      draggingOverTop,
      dragItemId,
      dropItem,
    };
  },
  emits: ['input'],
  components: { SorterItem },
  data() {
    return { orders: [] };
  },
  computed: {
    fixedSortOptions() {
      return this.sortOptions.map((so) => ({ ...so, desc: so.desc === undefined ? false : so.desc }));
    },
    firstAddableEdge() {
      return this.fixedSortOptions.find((option) => typeof this.orders.find((fromOrders) => orderEntitiesEqual(option, fromOrders)) === 'undefined');
    },
    hasAddableEdge() {
      return this.firstAddableEdge !== undefined;
    },
    mappedOrders() {
      return this.orders.map((o) => ({ ...o, key: this.getKey(o) }));
    },
  },
  methods: {
    handleDrop() {
      this.$emit('input', this.dropItem(this.mappedOrders));
    },
    getKey(order) {
      if (order.langs !== undefined && order.langs.length === 1) {
        return order.langs[0];
      }
      return order.attr;
    },
    add() {
      this.orders.push(this.firstAddableEdge);
    },
    change(index, value) {
      this.orders.splice(
        index,
        1,
        value,
      );
    },
    deleteOrder(index) {
      this.orders.splice(
        index,
        1,
      );
    },
  },
  watch: {
    orders: {
      handler(val) {
        if (isEqual(val, this.value)) {
          return;
        }
        this.$emit('input', copy(val));
      },
      deep: true,
    },
    value: {
      handler(val) {
        if (isEqual(val, this.orders)) {
          return;
        }
        this.orders = copy(val);
      },
      deep: true,
    },
  },
  created() {
    this.orders = copy(this.value);
  },
};
</script>

<style scoped lang="scss" type="text/scss">
  .sorter-builder {
    min-width: 20rem;
    display: flex;
    flex-direction: column;
    gap: .8rem;

    ._items {
      display: flex;
      flex-direction: column;
      gap: .8rem;
    }

    ._empty {
      color: $font-color-tertiary;
    }
  }
</style>
